'use client';

import { useMemoizedFn, useSetState } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useMemo, useState } from 'react';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { ProductLineAiVideo } from '@/app/components/icons/ProductLineAiVideo';
import { BasicTextarea } from '@/app/components/shared/BasicTextarea';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { toast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { useDevice, useGlobalModal, useRunTempUploadBatch } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type {
  AudioUploadFile,
  CreationWithTask,
  FormAudioPrompt,
  ToolTaskFormVideoPrompt,
  VideoUploadFile,
} from '@/app/types';
import {
  countLipSyncTextWeightedLength,
  estimateLipSyncTextDurationSeconds,
  isExtendTask,
  isVideoDurationWithinRange,
  normalizeDiscreteVideoDurationToSeconds,
  trimLipSyncTextToMaxWeightedLength,
} from '@/app/utils';
import { convertToolTask } from '@/app/utils/client';
import { CreationSelectorDialog } from '../_components/CreationSelectorDialog';
import { ExampleMediaPreview } from '../_components/ExampleMediaPreview';
import { ToolAudioUpload } from '../_components/ToolAudioUpload';
import { ToolFormContainer } from '../_components/ToolFormContainer';
import { ToolFormSubmitButton } from '../_components/ToolFormSubmitButton';
import { ToolMediaUpload } from '../_components/ToolMediaUpload';
import { ToolMediaUploadHeader } from '../_components/ToolMediaUploadHeader';
import { useCreationConsent } from '../_hooks/use-creation-consent';
import { useToolForm } from '../_hooks/use-tool-form';
import {
  createPromptListHelpers,
  createToolAudioPromptFromUploadFile,
  createToolVideoPromptFromUploadFile,
  createVideoPromptFromCreation,
} from '../_utils';
import { getToolTempAudioPendingItems, getToolTempVideosPendingItems } from '../_utils/tool-temp-upload-items';
import { LipSyncSettings } from './_components/LipSyncSettings';

interface LipSyncState {
  selectedSourceCreation?: CreationWithTask;
}

const MAX_VIDEO_FILE_SIZE = 500 * 1024 * 1024;
const MIN_VIDEO_DURATION = 1;
const MAX_VIDEO_DURATION = 120;

const MIN_AUDIO_DURATION = 1;
const MAX_AUDIO_DURATION = 120;
const MAX_AUDIO_FILE_SIZE = 20 * 1024 * 1024;

const MIN_TEXT_LENGTH = 4;
const MAX_TEXT_LENGTH = 1200;

export type LipSyncInputMode = 'text' | 'audio';

const LipSyncForm: FC = () => {
  const t = useTranslations();
  const { isMobile } = useDevice();
  const storeApi = useStoreApi();
  const { settings, updateSettings } = useToolForm('lip_sync');

  const [inputMode, setInputMode] = useState<LipSyncInputMode>('text');
  const [openSourceVideoSelector, setOpenSourceVideoSelector] = useState(false);
  const [selectedSourceCreationIndex, setSelectedSourceCreationIndex] = useState<number | null>(null);
  const [lipSyncState, setLipSyncState] = useSetState<LipSyncState>({
    selectedSourceCreation: undefined,
  });

  const { open: openSimpleCreationDetailModal } = useGlobalModal('simpleCreationDetailModal');

  const { updateLipSyncFormParams, lipSyncFormParams, videoPrompts, audioPrompts, textPrompt, userInfo } = useStore(
    (state) => ({
      updateLipSyncFormParams: state.updateLipSyncFormParams,
      lipSyncFormParams: state.lipSyncFormParams,
      videoPrompts: state.lipSyncFormParams.videoPrompts,
      audioPrompts: state.lipSyncFormParams.audioPrompts,
      textPrompt: state.lipSyncFormParams.textPrompt || '',
      userInfo: state.userInfo,
    }),
  );

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp videos',
    (refs) =>
      getToolTempVideosPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().lipSyncFormParams.videoPrompts,
          (prompts) => updateLipSyncFormParams('videoPrompts', prompts.filter(Boolean) as ToolTaskFormVideoPrompt[]),
        ),
        { videoPrompts },
      ),
    [updateLipSyncFormParams, userInfo, videoPrompts],
  );

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp audio',
    (refs) =>
      getToolTempAudioPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().lipSyncFormParams.audioPrompts,
          (prompts) => updateLipSyncFormParams('audioPrompts', prompts.filter(Boolean) as FormAudioPrompt[]),
        ),
        { audioPrompts },
      ),
    [audioPrompts, updateLipSyncFormParams, userInfo],
  );

  const sourceVideoPrompt = videoPrompts?.[0] || null;
  const audioPrompt = audioPrompts[0] || null;
  const ensureVoiceConsent = useCreationConsent(userInfo?.id, { requireVoiceConsent: SITE === 'china' });

  const handleSelectSourceVideo = useCallback(
    (index: number | null, creation?: CreationWithTask) => {
      if (!creation) {
        setSelectedSourceCreationIndex(null);
        setLipSyncState({
          selectedSourceCreation: undefined,
        });
        return;
      }

      setSelectedSourceCreationIndex(index);
      setLipSyncState({
        selectedSourceCreation: creation,
      });
    },
    [setLipSyncState],
  );

  const handleRemoveSourceVideo = useCallback(() => {
    updateLipSyncFormParams('videoPrompts', []);
  }, [updateLipSyncFormParams]);

  const handleSourceVideoUpload = useMemoizedFn((file: VideoUploadFile) => {
    const videoPrompt = createToolVideoPromptFromUploadFile(file);
    const newVideoPrompts = [...videoPrompts];
    newVideoPrompts[0] = videoPrompt;
    updateLipSyncFormParams('videoPrompts', newVideoPrompts);

    if (file.status === 'error' || file.status === 'removed') {
      handleRemoveSourceVideo();
    }
  });

  const handleRemoveAudio = useCallback(() => {
    updateLipSyncFormParams('audioPrompts', []);
  }, [updateLipSyncFormParams]);

  const handleAudioUpload = useMemoizedFn((file: AudioUploadFile) => {
    if (file.status === 'error' || file.status === 'removed') {
      handleRemoveAudio();
      return;
    }
    if (file.status === 'uploading' && inputMode === 'text') {
      setInputMode('audio');
    }
    updateLipSyncFormParams('audioPrompts', [createToolAudioPromptFromUploadFile(file)]);
  });

  const handleInputModeChange = useCallback((mode: LipSyncInputMode) => {
    setInputMode(mode);
  }, []);

  const textWeightedLength = useMemo(() => countLipSyncTextWeightedLength(textPrompt), [textPrompt]);

  const handleTextPromptChange = useMemoizedFn((value: string) => {
    const trimmed = trimLipSyncTextToMaxWeightedLength(value, MAX_TEXT_LENGTH);
    if (trimmed !== value) {
      toast({
        type: 'warning',
        message: t('create.createForm.error.invalidLength', { limit: MAX_TEXT_LENGTH }),
      });
    }
    updateLipSyncFormParams('textPrompt', trimmed);
  });

  const handleVoiceChange = useCallback(
    (voiceId: string, isMyVoice: boolean) => {
      updateSettings({ voice_id: voiceId });

      const nextExtraParams = { ...lipSyncFormParams.extraParams };
      if (isMyVoice) {
        nextExtraParams.is_my_voice = true;
      } else {
        delete nextExtraParams.is_my_voice;
      }
      updateLipSyncFormParams('extraParams', Object.keys(nextExtraParams).length ? nextExtraParams : undefined);
    },
    [lipSyncFormParams.extraParams, updateLipSyncFormParams, updateSettings],
  );

  const isVideoUploading = videoPrompts.some((prompt) => prompt.status === 'uploading');
  const isAudioUploading = audioPrompts.some((prompt) => prompt.status === 'uploading');
  const hasValidVideo = sourceVideoPrompt?.status === 'done' && !!sourceVideoPrompt.prompt;
  const hasValidAudio = audioPrompt?.status === 'done' && !!audioPrompt.prompt;
  const isTextValid = textWeightedLength >= MIN_TEXT_LENGTH && textWeightedLength <= MAX_TEXT_LENGTH;
  const hasSelectedVoice = !!settings?.voice_id;

  const hasValidInput = inputMode === 'text' ? isTextValid && hasSelectedVoice : hasValidAudio;

  const submitValue = useMemo(() => {
    const filteredFormParams = { ...lipSyncFormParams };
    const videoDurationSeconds = normalizeDiscreteVideoDurationToSeconds(sourceVideoPrompt?.prompt?.duration);
    const audioDurationRaw = audioPrompt?.prompt?.duration;
    const useAudioDuration =
      inputMode === 'audio' &&
      hasValidAudio &&
      typeof audioDurationRaw === 'number' &&
      Number.isFinite(audioDurationRaw);
    const resolvedDurationSeconds = useAudioDuration
      ? normalizeDiscreteVideoDurationToSeconds(audioDurationRaw)
      : inputMode === 'text'
        ? estimateLipSyncTextDurationSeconds(textPrompt, MIN_VIDEO_DURATION, MAX_VIDEO_DURATION)
        : videoDurationSeconds;

    filteredFormParams.settings = {
      ...filteredFormParams.settings,
      duration: resolvedDurationSeconds,
    };

    if (inputMode === 'text') {
      filteredFormParams.audioPrompts = [];
    } else {
      filteredFormParams.textPrompt = '';
      filteredFormParams.extraParams = undefined;
      const { voice_id: _v, speed: _s, volume: _vol, ...rest } = filteredFormParams.settings;
      filteredFormParams.settings = rest;
    }
    return convertToolTask({ taskType: 'lip_sync', formParams: filteredFormParams });
  }, [lipSyncFormParams, inputMode, sourceVideoPrompt, audioPrompt, hasValidAudio, textPrompt]);

  const disabledTip = useMemo(() => {
    if (isVideoUploading || (inputMode === 'audio' && isAudioUploading)) {
      return t('create.createForm.error.uploadingMedia');
    }
    if (!hasValidVideo) {
      return t('tools.lipSync.submit.disabledMissingVideo');
    }
    if (inputMode === 'text' && !isTextValid) {
      return t('tools.lipSync.submit.disabledInvalidText');
    }
    if (inputMode === 'text' && !hasSelectedVoice) {
      return t('tools.lipSync.submit.disabledMissingVoice');
    }
    if (!hasValidInput) {
      return t('tools.lipSync.submit.disabledMissingAudio');
    }
    return '';
  }, [isVideoUploading, isAudioUploading, hasValidVideo, isTextValid, hasSelectedVoice, hasValidInput, inputMode, t]);

  const remainingVideoSlots = sourceVideoPrompt ? 0 : 1;
  const remainingAudioSlots = audioPrompt ? 0 : 1;

  return (
    <>
      <ToolFormContainer
        className="bg-[#0a0d10] px-5 py-10"
        title={t('tools.lipSync.title')}
        description={t('tools.lipSync.description')}
        exampleSection={<ExampleMediaPreview tool="lip_sync" />}
        mediaSection={
          <MediaDropZone
            className="flex flex-col gap-5"
            acceptVideo={true}
            acceptAudio={true}
            acceptImage={false}
            maxVideoCount={remainingVideoSlots}
            maxVideoSize={MAX_VIDEO_FILE_SIZE}
            maxVideoDuration={MAX_VIDEO_DURATION}
            minVideoDuration={MIN_VIDEO_DURATION}
            maxAudioCount={remainingAudioSlots}
            maxAudioDuration={MAX_AUDIO_DURATION}
            minAudioDuration={MIN_AUDIO_DURATION}
            maxAudioSize={MAX_AUDIO_FILE_SIZE}
            disabled={remainingVideoSlots === 0 && remainingAudioSlots === 0}
            allowTempMode={true}
            onChange={(file, type) => {
              if (type === 'video') handleSourceVideoUpload(file as VideoUploadFile);
              else if (type === 'audio') handleAudioUpload(file as AudioUploadFile);
            }}
          >
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <ToolMediaUpload
                type="video"
                header={
                  <ToolMediaUploadHeader
                    icon={<ProductLineAiVideo className="size-5 text-system-white" />}
                    title={t('tools.lipSync.videoUpload.label')}
                    extra={
                      <CustomTooltip content={t('tools.lipSync.videoUpload.labelTooltip')} side="top">
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center text-system-white48 transition-colors hover:text-system-white"
                          aria-label={t('tools.lipSync.videoUpload.labelTooltipAria')}
                        >
                          <BaseLineInfo className="size-4" />
                        </button>
                      </CustomTooltip>
                    }
                  />
                }
                surfaceSize="sm"
                videoPrompt={sourceVideoPrompt}
                isMobile={isMobile}
                maxVideoSize={MAX_VIDEO_FILE_SIZE}
                maxDuration={MAX_VIDEO_DURATION}
                minDuration={MIN_VIDEO_DURATION}
                onVideoUpload={handleSourceVideoUpload}
                onOpenSelector={() => setOpenSourceVideoSelector(true)}
                onRemoveMedia={handleRemoveSourceVideo}
              />
              <div className="text-system-white/40 text-xs leading-[1.4]">{t('tools.lipSync.videoUpload.helper')}</div>
            </div>

            <div className="space-y-3">
              <ToolMediaUploadHeader
                title={t('tools.lipSync.input.label')}
                extra={
                  <CustomTooltip content={t('tools.lipSync.input.labelTooltip')} side="top">
                    <button
                      type="button"
                      className="inline-flex shrink-0 items-center text-system-white48 transition-colors hover:text-system-white"
                      aria-label={t('tools.lipSync.input.labelTooltipAria')}
                    >
                      <BaseLineInfo className="size-4" />
                    </button>
                  </CustomTooltip>
                }
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  className={`rounded-8 px-4 py-2 text-xs transition-colors ${
                    inputMode === 'text'
                      ? 'bg-system-white text-system-black'
                      : 'border border-system-white08 bg-system-white04 text-system-white/60 hover:text-system-white'
                  }`}
                  onClick={() => handleInputModeChange('text')}
                >
                  {t('tools.lipSync.input.textMode')}
                </button>
                <button
                  type="button"
                  className={`rounded-8 px-4 py-2 text-xs transition-colors ${
                    inputMode === 'audio'
                      ? 'bg-system-white text-system-black'
                      : 'border border-system-white08 bg-system-white04 text-system-white/60 hover:text-system-white'
                  }`}
                  onClick={() => handleInputModeChange('audio')}
                >
                  {t('tools.lipSync.input.audioMode')}
                </button>
              </div>

              {inputMode === 'text' ? (
                <div className="space-y-2">
                  <div className="relative flex min-h-[120px] w-full flex-col rounded-16 border border-system-white08 bg-system-white04 p-3 backdrop-blur-[30px]">
                    <BasicTextarea
                      placeholder={t('tools.lipSync.input.textPlaceholder')}
                      textPrompt={textPrompt}
                      updateTextPrompt={handleTextPromptChange}
                      showClearButton={!!textPrompt}
                      onClearPrompt={() => updateLipSyncFormParams('textPrompt', '')}
                      maxLength={false}
                    />
                  </div>
                  <div className="flex justify-end text-system-white/40 text-xs leading-[1.4]">
                    {textWeightedLength}/{MAX_TEXT_LENGTH}
                  </div>
                </div>
              ) : (
                <ToolAudioUpload
                  audioPrompt={audioPrompt}
                  onAudioUpload={handleAudioUpload}
                  onRemoveAudio={handleRemoveAudio}
                  helperText={t('tools.lipSync.input.audioHelper')}
                  minDuration={MIN_AUDIO_DURATION}
                  maxDuration={MAX_AUDIO_DURATION}
                  maxSize={MAX_AUDIO_FILE_SIZE}
                />
              )}
            </div>
          </MediaDropZone>
        }
        settingsSection={
          inputMode === 'text' ? (
            <LipSyncSettings
              voice={settings?.voice_id as string | undefined}
              speed={(settings?.speed as number) ?? 1.0}
              volume={(settings?.volume as number) ?? 0}
              onVoiceChange={handleVoiceChange}
              onSpeedChange={(value) => updateSettings({ speed: value })}
              onVolumeChange={(value) => updateSettings({ volume: value })}
            />
          ) : undefined
        }
        submitButton={
          <ToolFormSubmitButton
            taskType="lip_sync"
            submitValue={submitValue}
            disabled={!!disabledTip}
            disabledTip={disabledTip}
            onBeforeSubmit={ensureVoiceConsent}
          >
            {t('create.createForm.submit')}
          </ToolFormSubmitButton>
        }
      />

      <CreationSelectorDialog
        open={openSourceVideoSelector}
        onOpenChange={setOpenSourceVideoSelector}
        selectedIndex={selectedSourceCreationIndex}
        mediaType="video"
        maxDuration={MAX_VIDEO_DURATION}
        minDuration={MIN_VIDEO_DURATION}
        onSelectionChange={handleSelectSourceVideo}
        onConfirm={() => {
          setOpenSourceVideoSelector(false);
          const selectedCreation = lipSyncState.selectedSourceCreation;
          const selectedDuration =
            (isExtendTask(selectedCreation?.task.type)
              ? selectedCreation?.duration
              : selectedCreation?.task.settings.duration) || 0;

          if (
            selectedCreation &&
            isVideoDurationWithinRange({
              duration: selectedDuration,
              minDuration: MIN_VIDEO_DURATION,
              maxDuration: MAX_VIDEO_DURATION,
            })
          ) {
            updateLipSyncFormParams('videoPrompts', [
              createVideoPromptFromCreation(selectedCreation, {
                includeReference: true,
              }),
            ]);
          }
        }}
        onViewCreation={(creation) => {
          openSimpleCreationDetailModal({
            creation,
          });
        }}
      />
    </>
  );
};

export default LipSyncForm;
